import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { toggleLoginModal as toggleLoginModalPlatform } from 'platform/site-wide/user-nav/actions';

export const AdditionalMarriagesAlert = () => (
  <va-alert slim status="warning" disable-border="true">
    <p className="vads-u-margin-y--0">
      You’ll need to submit a Statement in Support of Claim (VA Form 21-4138) as
      needed to provide information for each additional marriage. For each
      marriage, include the start and end dates, as well as the reason the
      marriage ended.
    </p>
    <p>
      You can upload this document at the end of this application. Or you can
      send it to us by mail.
    </p>
    <p>
      <va-link
        href="/find-forms/about-form-21-4138/"
        external
        text="Get VA Form 21-4138 to download"
      />
    </p>
  </va-alert>
);

const RequestFormAlert = ({
  title, // Include a or an, e.g. "a Nursing Home" or "an Examination"
  formName,
  submitOnline,
  onlineLink,
  onlineLinkText,
  formLink,
  advisory,
  children,
}) => (
  <va-alert slim status="warning">
    <p className="vads-u-margin-y--0">
      You’ll need to submit {title} ({formName}
      ). {advisory}
    </p>
    <p>{children}</p>
    {submitOnline ? (
      <p>
        You can submit this form online, upload it at the end of this
        application, or send it to us by mail.
      </p>
    ) : (
      <p>
        You can upload this document at the end of this application. Or you can
        send it to us by mail.
      </p>
    )}
    {onlineLink && onlineLinkText && (
      <p>
        <va-link href={onlineLink} external text={`${onlineLinkText}`} />
      </p>
    )}
    <p>
      <va-link href={formLink} external text={`Get ${formName} to download`} />
    </p>
  </va-alert>
);

RequestFormAlert.propTypes = {
  formLink: PropTypes.string.isRequired,
  formName: PropTypes.string.isRequired,
  submitOnline: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  advisory: PropTypes.string,
  children: PropTypes.node,
  onlineLink: PropTypes.string,
  onlineLinkText: PropTypes.string,
};

export const RequestNursingHomeInformationAlert = () => (
  <RequestFormAlert
    title="a Request for Nursing Home Information in Connection with Claim for Aid and Attendance"
    formName="VA Form 21-0779"
    formLink="https://www.va.gov/find-forms/about-form-21-0779/"
    advisory="A nursing home official must complete this form."
    onlineLink="https://www.va.gov/supporting-forms-for-claims/submit-nursing-home-information-form-21-0779"
    onlineLinkText="Submit request for nursing home information in connection with claim for Aid and Attendance"
    submitOnline
  />
);

export const SpecialMonthlyPensionEvidenceAlert = () => (
  <RequestFormAlert
    title="an Examination of Housebound Status or Permanent
    Need for Regular Aid and Attendance"
    formName="VA Form 21-2680"
    formLink="https://www.va.gov/find-forms/about-form-21-2680/"
    advisory="A licensed medical professional must complete this form."
    onlineLink="https://www.va.gov/pension/aid-attendance-housebound/apply-form-21-2680"
    onlineLinkText="Apply for Aid and Attendance benefits or Housebound allowance"
    submitOnline={false}
  />
);

export const IncomeAssetStatementFormAlert = () => (
  <RequestFormAlert
    title="an Income and Asset Statement in Support of Claim for Pension or Parents' Dependency and Indemnity Compensation"
    formName="VA Form 21P-0969"
    formLink="https://www.va.gov/find-forms/about-form-21p-0969/"
    onlineLink="https://www.va.gov/supporting-forms-for-claims/submit-income-and-asset-statement-form-21p-0969"
    onlineLinkText="Submit income and asset statement to support your claim"
    submitOnline
  />
);

export const CourtOrderSeparationAlert = () => (
  <va-alert slim status="warning" disable-border="true">
    <p className="vads-u-margin-y--0">
      You’ll need to submit a copy of the court order.
    </p>
    <p>
      We’ll ask you to upload this document at the end of this application. Or
      you can send it to us by mail.
    </p>
  </va-alert>
);

export const UnauthenticatedWarningAlert = ({
  toggleLoginModal = toggleLoginModalPlatform,
}) => {
  const isLoggedIn = useSelector(state => state.user.login.currentlyLoggedIn);
  const dispatch = useDispatch();
  const numberOfSteps = 8;
  const showLoginModal = e => {
    e.preventDefault();
    dispatch(toggleLoginModal(true));
  };

  if (isLoggedIn) {
    return null;
  }
  return (
    <va-alert status="warning" uswds slim>
      <p className="vads-u-margin-y--0">
        This application is {numberOfSteps} steps long and it contains several
        substeps per step. We advise you {}
        <va-link
          href="#"
          onClick={showLoginModal}
          text="sign in to save your progress"
        />
        .
      </p>
      <p>
        <strong>Note:</strong> You can sign in after you start your application.
        But you’ll lose any information you already filled in.
      </p>
    </va-alert>
  );
};

UnauthenticatedWarningAlert.propTypes = {
  isLoggedIn: PropTypes.bool,
  toggleLoginModal: PropTypes.func,
};

export const handleAlertMaxItems = () => (
  <>
    <ul>
      <li className="vads-u-margin-bottom--2">
        <span className="vads-u-display--block">
          Statement in Support of Claim (VA Form 21-4138)
        </span>
        <va-link
          href="https://www.va.gov/find-forms/about-form-21-4138/"
          external
          text="Get VA Form 21-4138 to download"
        />
      </li>
      <li>
        <span className="vads-u-display--block">
          Application Request to Add and/or Remove Dependents (VA Form 21-686c)
        </span>
        <span className="vads-u-display--block vads-u-margin-bottom--2">
          <va-link
            href="https://www.va.gov/manage-dependents/add-remove-form-21-686c-674"
            external
            text="Add or remove dependents on VA benefits"
          />
        </span>
        <span className="vads-u-display--block">
          <va-link
            href="https://www.va.gov/find-forms/about-form-21-686c/"
            external
            text="Get VA Form 21-686c to download"
          />
        </span>
      </li>
    </ul>
    <p>
      You can submit VA Form 21-686c online. Both documents can be uploaded at
      the end of this application or sent to us by mail.
    </p>
  </>
);

export const handleVeteranMaxMarriagesAlert = () => (
  <div>
    <p className="vads-u-margin-top--0">
      You have added the maximum number of allowed Veteran’s previous marriages
      for this application. Additional marriages can be added using one of these
      supporting documents.
    </p>
    {handleAlertMaxItems()}
  </div>
);

export const handleSpouseMaxMarriagesAlert = () => (
  <div>
    <p className="vads-u-margin-top--0">
      You have added the maximum number of allowed previous marriages for this
      application. Additional marriages can be added using one of these
      supporting documents.
    </p>
    {handleAlertMaxItems()}
  </div>
);

export const AdditionalDependentsAlert = () => (
  <va-alert slim status="warning" disable-border="true">
    <p className="vads-u-margin-y--0">
      You’ll need to submit a Statement in Support of Claim (VA Form 21-4138)
      with the name of the person each child is currently living with and the
      full address where each child resides.
    </p>
    <p>
      You can upload this document at the end of this application. Or you can
      send it to us by mail.
    </p>
    <p>
      <span className="vads-u-display--block">
        <va-link
          href="https://www.va.gov/find-forms/about-form-21-4138/"
          external
          text="Get VA Form 21-4138 to download"
        />
      </span>
    </p>
  </va-alert>
);

export const GrossMonthlyIncomeAlert = () => (
  <va-alert close-btn-aria-label="Close notification" status="info" visible>
    <h4 slot="headline">You only need to report Social Security income</h4>
    <p className="vads-u-margin-y--0">
      All other income should be reported on an Income and Asset Statement in
      Support of Claim for Pension or Parents' Dependency and Indemnity
      Compensation (VA Form 21P-0969).
    </p>
  </va-alert>
);
